import { EventConstructorArgs } from '@linktr.ee/event-lib/envelope/Event';
import { type FormEvent, useCallback, useEffect, useRef } from 'react';
import { publishEvent, publishNonGoldenEvent } from './client';
import {
  type AuthPlatformType,
  createPageViewedEvent,
  createScreenViewedEvent,
  isDirectSignupIdentifierInputType,
  publishAuthJourneyContinuedEvent,
  publishSignupIdentifierInputInteractedEvent,
  publishSignupStartedEvent,
} from './events';

const INITIAL_ANALYTICS_DELAY_MS = 250;

const reportInitialAnalyticsError = (error: unknown): void => {
  // eslint-disable-next-line no-console
  console.info('analytics:pushInitialEvents failed', error);
};

const safelyPublishInitialAnalyticsEvent = (callback: () => void): void => {
  try {
    callback();
  } catch (error) {
    reportInitialAnalyticsError(error);
  }
};

const scheduleInitialAnalyticsEvent = (callback: () => void): void => {
  if (typeof window === 'undefined') {
    safelyPublishInitialAnalyticsEvent(callback);
    return;
  }

  window.setTimeout(
    () => safelyPublishInitialAnalyticsEvent(callback),
    INITIAL_ANALYTICS_DELAY_MS
  );
};

export const usePublishScreenViewedEvent = (
  screenTitle: EventConstructorArgs<
    'ScreenViewedGolden',
    '14-13-0'
  >['payload']['screen']['screenTitle'],
  enabled = true,
  specialContentToken?: string
) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const event = createScreenViewedEvent(screenTitle, specialContentToken);

    scheduleInitialAnalyticsEvent(() => {
      publishEvent(event);
    });
  }, [enabled, screenTitle, specialContentToken]);
};

export const usePublishPageViewedEvent = (enabled = true) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    // TODO: we need to figure out how to detect mobile app webview with Descope
    // one Auth0, we only push PageViewed events when not in mobile app webview
    try {
      const event = createPageViewedEvent();

      if (event) {
        scheduleInitialAnalyticsEvent(() => {
          publishNonGoldenEvent(event);
        });
      }
    } catch (error) {
      reportInitialAnalyticsError(error);
    }
  }, [enabled]);
};

/**
 * Emits AuthJourneyContinued for a propagated journey from the
 * initial-analytics slot: destination browser/session context is ready by
 * then, it does not wait on pageview delivery, and it emits at most once per
 * landing (self-guarded). Mounted at the app root so every landing surface
 * is covered, not only screens that publish a pageview.
 */
export const usePublishAuthJourneyContinuedEvent = () => {
  useEffect(() => {
    scheduleInitialAnalyticsEvent(publishAuthJourneyContinuedEvent);
  }, []);
};

const getInputType = (event: Event): string | undefined => {
  if ('inputType' in event && typeof event.inputType === 'string') {
    return event.inputType;
  }
  return undefined;
};

const safelyPublishSignupIdentifierEvent = (callback: () => void): void => {
  try {
    callback();
  } catch {
    // Analytics must never block identifier entry or submission. Each event
    // publisher owns its own reporting, so this is a final flow-safety guard.
  }
};

export type SignupIdentifierInteractionState = {
  hasPublishedEmailSignupStart: boolean;
};

export type EmailSignupMethod = 'Email OTP' | 'Email Magic Link';

export const useSignupIdentifierInputInteraction = (
  platformType: AuthPlatformType,
  interactionStateRef?: { current: SignupIdentifierInteractionState },
  emailSignupMethod?: EmailSignupMethod
) => {
  const hasPublishedInteractionRef = useRef(false);
  const localInteractionStateRef = useRef<SignupIdentifierInteractionState>({
    hasPublishedEmailSignupStart: false,
  });
  const stateRef = interactionStateRef ?? localInteractionStateRef;

  const ensureEmailSignupStarted = useCallback(() => {
    if (stateRef.current.hasPublishedEmailSignupStart) {
      return;
    }

    stateRef.current.hasPublishedEmailSignupStart = true;
    safelyPublishSignupIdentifierEvent(() => {
      publishSignupStartedEvent(emailSignupMethod ?? 'Email OTP', platformType);
    });
  }, [platformType, stateRef, emailSignupMethod]);

  const handleInput = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      if (
        hasPublishedInteractionRef.current ||
        !isDirectSignupIdentifierInputType(getInputType(event.nativeEvent)) ||
        event.currentTarget.value.trim().length === 0
      ) {
        return;
      }

      hasPublishedInteractionRef.current = true;
      safelyPublishSignupIdentifierEvent(() => {
        publishSignupIdentifierInputInteractedEvent();
      });
      ensureEmailSignupStarted();
    },
    [ensureEmailSignupStarted]
  );

  return { handleInput, ensureEmailSignupStarted };
};
